import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, IconButton } from '@mui/material';
import Paper from '@mui/material/Paper';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import SaveButton from '../common/savebutton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import { cloneDeep } from 'lodash';
import {
    productTypeSaveCapacityAllocations,
    productTypeLoadCapacityAllocations,
    getProductTypes,
    upsertProductType,
    refreshProductTypes as refreshProductTypesFromServer,
    getCapacityGroups,
    upsertCapacityGroup,
} from '../modules/prodplanningdata';
import { buildCapacityByCapacityGroupAndWeekData } from './weeklycapacitydatabuilder';
import {
    formatWeekIdentifierDayjs,
    getWeekFromIdentifier,
    getYearFromIdentifier,
    generateContinuousWeekList,
} from '../order/capacityplannerproducttypes';
import CapacityGroupCapacityAllocation from '../domain/capacitygroupcapacityallocation';
import { useTranslation } from 'react-i18next';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import TextEditBlock from '../invoice/texteditblock';
import { toast } from 'react-toastify';

const sxTable = {
    width: '100%',
    marginBottom: '20px',
}

const sxProdcategories = {
    marginTop: '1em',
    marginBottom: '1em',
}

const sxAccordionDetails = {
    display: 'block',
}

const sxBtn = {
    margin: (theme) => theme.spacing(1),
}

const sxEdit = {}

const sxStickyCell = {
    position: 'sticky',
    left: 0,
    backgroundColor: 'background.paper',
    boxShadow: (theme) => `1px 0px ${theme.palette.divider}`
}

const ProductionPlanning2 = (props) => {
    const dispatch = useDispatch();
    const capacityAllocationsByWeek = useSelector((state) => state.prodplanningdata.productTypeCapacityAllocations);
    const loadingCapacityData = useSelector((state) => state.prodplanningdata.productTypeCapacityAllocationsLoading);
    const productTypes = useSelector((state) => state.prodplanningdata.productTypes);
    const productTypesLoading = useSelector((state) => state.prodplanningdata.productTypesLoading);
    const capacityGroups = useSelector((state) => state.prodplanningdata.capacityGroups);
    const capacityGroupsLoading = useSelector((state) => state.prodplanningdata.capacityGroupsLoading);
    const [edit, setEdit] = useState(false);

    // mutated data for view. Contains data from backend & possible changes by user
    const [weeklyCapacitiesPerType, setWeeklyCapacitiesPerType] = useState({});
    //const [urgentCapacity, setUrgentCapacity] = useState({});

    // contains item changes by user that are sent to backend in case data is saved
    const [changedItems, setChangedItems] = useState([]);

    const [weekList, setWeekList] = useState([]);
    const [startWeek, setStartWeek] = useState(null);
    const [endWeek, setEndWeek] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [hasChanges, setHasChanges] = useState([]);
    const [saving, setSaving] = useState(false);
    const [changingSelection, setChangingSelection] = useState(false);

    const [productTypeEdit, setProductTypeEdit] = useState({});
    const [productTypeSearchPreview, setProductTypeSearchPreview] = useState({});
    const [capacityGroupEdit, setCapacityGroupEdit] = useState({});

    const { t } = useTranslation();

    const products = useSelector((state) => state.productdata.filterProducts);

    // used to build the data for UI
    // inputs: backend data (capacityAllocationsByWeek) + potential changes by user (changedItems)
    useEffect(() => {
        if (!productTypes && !productTypesLoading) {
            getProductTypes()(dispatch);
        }
        if (!capacityGroups && !capacityGroupsLoading) {
            getCapacityGroups()(dispatch);
        }
        if (
            !productTypes ||
            !capacityAllocationsByWeek ||
            Object.keys(capacityAllocationsByWeek).length === 0 ||
            weekList.length === 0
        ) {
            return;
        }
        const capacityData = buildCapacityByCapacityGroupAndWeekData(
            capacityAllocationsByWeek,
            capacityGroups,
            weekList,
            changedItems
        );

        setWeeklyCapacitiesPerType(capacityData.capacityPerCapacityGroup);
        setChangingSelection(false);
    }, [
        capacityAllocationsByWeek,
        productTypes,
        weekList,
        changedItems,
        dispatch,
        productTypesLoading,
        capacityGroups,
        capacityGroupsLoading,
    ]);

    // used only to populate initial week list
    useEffect(() => {
        if (weekList.length === 0) {
            let start = dayjs().subtract(1, 'months');
            let end = dayjs().add(4, 'months');
            if (start.date() > 26) start = start.add(6, 'days');
            if (end.date() > 26) end = end.add(6, 'days');
            setStartWeek(`${formatWeekIdentifierDayjs(start)}`);
            setEndWeek(`${formatWeekIdentifierDayjs(end)}`);
            setStartDate(start);
            setEndDate(end);
            setWeekList(
                generateContinuousWeekList(formatWeekIdentifierDayjs(start), formatWeekIdentifierDayjs(end), true)
            );
        }
    }, [weekList.length]);

    useEffect(() => {
        if (changedItems && changedItems.length > 0) {
            setHasChanges(true);
        } else {
            setHasChanges(false);
        }
    }, [changedItems]);

    // makes sure that we have loaded all necessary data from backend
    useEffect(() => {
        if (loadingCapacityData) return;

        if (weekList && weekList.length > 0 && startWeek && endWeek && capacityAllocationsByWeek) {
            const sortedWeeks = weekList.sort();
            if (
                !capacityAllocationsByWeek[sortedWeeks[0]] ||
                !capacityAllocationsByWeek[sortedWeeks[sortedWeeks.length - 1]]
            ) {
                dispatch(productTypeLoadCapacityAllocations(startDate, endDate));
            } else {
                setChangingSelection(false);
            }
        } else if (
            weekList &&
            weekList.length &&
            Object.keys(capacityAllocationsByWeek).length === 0 &&
            startWeek &&
            endWeek
        ) {
            dispatch(productTypeLoadCapacityAllocations(startDate, endDate));
        }
    }, [weekList, capacityAllocationsByWeek, startWeek, endWeek, dispatch, loadingCapacityData, startDate, endDate]);

    useEffect(() => {
        if (products && products.length > 0 && productTypes && productTypes.length > 0) {
            const previews = {};
            const others = productTypes.find((t) => t.name === 'Muut');
            let sumOfTypes = 0;
            for (const prodType of productTypes) {
                // eslint-disable-next-line no-loop-func
                var matches = products.filter((p) => p.type === prodType.name);
                previews[prodType.id] = matches.length;
            }
            for (const k of Object.keys(previews)) {
                sumOfTypes += previews[k];
            }
            previews[others.id] = products.length - sumOfTypes;
            setProductTypeSearchPreview(previews);
        }
    }, [products, productTypes]);

    const onChange = (e, capacityGroupId, yearAndWeek) => {
        const year = getYearFromIdentifier(yearAndWeek);
        const week = getWeekFromIdentifier(yearAndWeek);
        const newValue = parseInt(e.target.value);
        const capacityGroup = capacityGroups.find((c) => c.id === capacityGroupId);
        const weeklyCapaForType = capacityAllocationsByWeek[yearAndWeek].find(
            (a) => a.capacityGroup.id === capacityGroupId
        );
        const newItems = cloneDeep(changedItems);
        const indInChangedItems = newItems.findIndex(
            (a) => a.capacityGroup.id === capacityGroupId && a.year === year && a.week === week
        );

        if (
            (weeklyCapaForType && weeklyCapaForType.allocatedCapacity !== newValue) ||
            (!weeklyCapaForType && newValue > 0)
        ) {
            // 1) there is capacity configured in backend data and we're about to change it
            //   --> add the changed value to "changedItems" state variable.
            //
            // 2) There is no configured capacity for the given portfolio and week -> add/update item
            //
            if (indInChangedItems !== -1) {
                newItems.splice(indInChangedItems, 1);
            }
            newItems.push(
                new CapacityGroupCapacityAllocation({
                    capacityGroup: capacityGroup,
                    year,
                    week,
                    allocatedCapacity: newValue,
                })
            );
        } else if ((weeklyCapaForType && weeklyCapaForType.allocatedCapacity === newValue) || !weeklyCapaForType) {
            // 1) there is capacity configured in the backend data and it is the same as
            // the edited value --> Make sure any change object are removed from "changedItems"
            //
            // 2) There is no configured capacity in backend for this case, and the value that is edited is null/0/empty
            //    --> make sure change item is cleaned out
            if (indInChangedItems !== -1) {
                newItems.splice(indInChangedItems, 1);
            }
        }
        setChangedItems(newItems);
    };

    const cancelEdit = () => {
        setChangedItems([]);
        setEdit(false);
    };

    const saveChanges = async () => {
        setSaving(true);
        await productTypeSaveCapacityAllocations(changedItems)(dispatch);
        setChangedItems([]);
        setSaving(false);
    };

    const changeSelection = async (months) => {
        setChangingSelection(true);
        let start = startDate.add(months, 'months');
        let end = endDate.add(months, 'months');
        if (start.date() > 26) start = start.add(6, 'days');
        if (end.date() > 26) end = end.add(6, 'days');
        setStartWeek(`${formatWeekIdentifierDayjs(start)}`);
        setEndWeek(`${formatWeekIdentifierDayjs(end)}`);
        setStartDate(start);
        setEndDate(end);
        setWeekList(generateContinuousWeekList(formatWeekIdentifierDayjs(start), formatWeekIdentifierDayjs(end), true));
    };

    const refreshProductTypes = async () => {
        await refreshProductTypesFromServer()(dispatch);
    };

    const editCapacityGroup = (id) => {
        const group = capacityGroups.find((g) => g.id === id);
        setCapacityGroupEdit({ [id]: { ...group } });
    };

    const onChangeCapacityGroupUrgentPct = (e) => {
        const id = Object.keys(capacityGroupEdit)[0];
        const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
        setCapacityGroupEdit({ [id]: { ...capacityGroupEdit[id], urgentCapacityPercentage: value } });
    };

    const saveCapacityGroup = async (id) => {
        await upsertCapacityGroup(capacityGroupEdit[id])(dispatch);
        setCapacityGroupEdit({});
    };

    const editProductType = (id) => {
        const edits = {};
        edits[id] = productTypes.find((t) => t.id === id);
        setProductTypeEdit(edits);
    };

    const onChangeProdType = (e) => {
        const newValue = e.target.value;
        const field = e.target.name;
        const id = Object.keys(productTypeEdit)[0];
        const edits = cloneDeep(productTypeEdit);
        edits[id][field] = newValue;
        setProductTypeEdit(edits);
    };

    const onChangeCapacityGroupName = (e) => {
        const newValue = e.target.value;
        if (newValue.toLowerCase() === 'kokonaiskapasiteetti') {
            toast.error(t('toast.capacityGroupNameTotalCapacityRestricted'), {
                autoClose: 5000,
                hideProgressBar: false,
            });
            return;
        }
        const id = Object.keys(productTypeEdit)[0];
        const edits = cloneDeep(productTypeEdit);
        edits[id].capacityGroup['name'] = newValue;
        setProductTypeEdit(edits);
    };

    if (!weeklyCapacitiesPerType || !productTypes || !capacityGroups) return null;

    return (
        <Grid item xs={12}>
            <Grid item xs={12} sx={sxProdcategories}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Grid container justifyContent="space-between">
                            <Grid item>
                                <strong>
                                    {t('admin.productTypes')} ({productTypes.length})
                                </strong>
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails sx={sxAccordionDetails}>
                        <Table sx={sxTable} size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('admin.productType')}</TableCell>
                                    <TableCell>{t('admin.capacityGroup')}</TableCell>
                                    <TableCell>{t('prodplanning.minimunDeliveryTimeDays')}</TableCell>
                                    <TableCell>{t('prodplanning.addDaysToOrder')}</TableCell>
                                    <TableCell>{t('prodplanning.maxPcsPerOrderPerDay')}</TableCell>
                                    <TableCell>{t('general.products') + ' ' + t('pcs')}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {productTypes.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.name}</TableCell>
                                        {!productTypeEdit[item.id] && <TableCell>{item.capacityGroup.name}</TableCell>}
                                        {productTypeEdit[item.id] && (
                                            <TableCell>
                                                <TextEditBlock
                                                    name="capacityGroup"
                                                    sx={sxEdit}
                                                    value={productTypeEdit[item.id].capacityGroup.name}
                                                    onChange={onChangeCapacityGroupName}
                                                />
                                            </TableCell>
                                        )}
                                        {!productTypeEdit[item.id] && (
                                            <TableCell>{item.minimumDeliveryTimeInDays}</TableCell>
                                        )}
                                        {productTypeEdit[item.id] && (
                                            <TableCell>
                                                <TextEditBlock
                                                    name="minimumDeliveryTimeInDays"
                                                    sx={sxEdit}
                                                    value={productTypeEdit[item.id].minimumDeliveryTimeInDays}
                                                    number={true}
                                                    onChange={onChangeProdType}
                                                />
                                            </TableCell>
                                        )}
                                        {!productTypeEdit[item.id] && (
                                            <TableCell>{item.additionalDeliveryDelayInDays}</TableCell>
                                        )}
                                        {productTypeEdit[item.id] && (
                                            <TableCell>
                                                <TextEditBlock
                                                    name="additionalDeliveryDelayInDays"
                                                    sx={sxEdit}
                                                    value={productTypeEdit[item.id].additionalDeliveryDelayInDays}
                                                    number={true}
                                                    onChange={onChangeProdType}
                                                />
                                            </TableCell>
                                        )}
                                        {!productTypeEdit[item.id] && (
                                            <TableCell>
                                                {0 === item.maxQuantityPerOrderPerDay
                                                    ? ''
                                                    : item.maxQuantityPerOrderPerDay}
                                            </TableCell>
                                        )}
                                        {productTypeEdit[item.id] && (
                                            <TableCell>
                                                <TextEditBlock
                                                    name="maxQuantityPerOrderPerDay"
                                                    sx={sxEdit}
                                                    value={productTypeEdit[item.id].maxQuantityPerOrderPerDay}
                                                    number={true}
                                                    onChange={onChangeProdType}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>{productTypeSearchPreview[item.id]}</TableCell>
                                        {!productTypeEdit[item.id] && (
                                            <TableCell>
                                                <IconButton
                                                    color="secondary"
                                                    onClick={() => editProductType(item.id)}
                                                    size={'small'}>
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        {productTypeEdit[item.id] && (
                                            <TableCell>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    sx={sxBtn}
                                                    onClick={async () => {
                                                        await upsertProductType(productTypeEdit[item.id])(dispatch);
                                                        if (
                                                            capacityGroups.findIndex(
                                                                (c) =>
                                                                    c.name ===
                                                                    productTypeEdit[item.id].capacityGroup.name
                                                            ) === -1
                                                        ) {
                                                            await getCapacityGroups()(dispatch);
                                                        }
                                                        setProductTypeEdit({});
                                                    }}>
                                                    {t('buttons.save')}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    sx={sxBtn}
                                                    onClick={() => {
                                                        setProductTypeEdit({});
                                                    }}>
                                                    {t('buttons.cancel')}
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <br />
                        <Grid item xs={12}>
                            <IconButton onClick={refreshProductTypes} disabled={productTypesLoading}>
                                <RefreshIcon />
                            </IconButton>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            <Grid item xs={12} sx={sxProdcategories}>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <strong>{t('admin.capacityGroups')}</strong>
                    </AccordionSummary>
                    <AccordionDetails sx={sxAccordionDetails}>
                        <Table sx={sxTable} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('admin.capacityGroup')}</TableCell>
                                    <TableCell>{t('prodplanning.urgentCapacityPercentage')}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {capacityGroups.map((group) => (
                                    <TableRow key={group.id}>
                                        <TableCell>{group.name}</TableCell>
                                        {!capacityGroupEdit[group.id] && (
                                            <TableCell>{group.urgentCapacityPercentage}%</TableCell>
                                        )}
                                        {capacityGroupEdit[group.id] && (
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    inputProps={{ min: 0, max: 100 }}
                                                    value={capacityGroupEdit[group.id].urgentCapacityPercentage}
                                                    onChange={onChangeCapacityGroupUrgentPct}
                                                />
                                            </TableCell>
                                        )}
                                        {!capacityGroupEdit[group.id] && (
                                            <TableCell>
                                                <IconButton
                                                    color="secondary"
                                                    onClick={() => editCapacityGroup(group.id)}
                                                    size={'small'}>
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                        {capacityGroupEdit[group.id] && (
                                            <TableCell>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                   sx={sxBtn}
                                                    onClick={() => saveCapacityGroup(group.id)}>
                                                    {t('buttons.save')}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                   sx={sxBtn}
                                                    onClick={() => setCapacityGroupEdit({})}>
                                                    {t('buttons.cancel')}
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            <TableContainer component={Paper}>
                <Table sx={sxTable} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={sxStickyCell}>{t('admin.capacityGroup')}</TableCell>
                            {weekList.map((w) => (
                                <TableCell key={w}>{w}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {capacityGroups.map((capacityGroup) => (
                            <TableRow key={capacityGroup.id}>
                                <TableCell component="th" scope="row" sx={sxStickyCell}>
                                    {capacityGroup.name}
                                </TableCell>
                                {!edit &&
                                    weekList.map((w) => (
                                        <TableCell key={w}>
                                            {weeklyCapacitiesPerType && weeklyCapacitiesPerType[capacityGroup.id] && (
                                                <span>
                                                    {weeklyCapacitiesPerType[capacityGroup.id].capacities[w]
                                                        ? weeklyCapacitiesPerType[capacityGroup.id].capacities[w]
                                                        : '0'}
                                                </span>
                                            )}
                                        </TableCell>
                                    ))}
                                {edit &&
                                    weekList.map((w) => (
                                        <TableCell key={w}>
                                            {weeklyCapacitiesPerType && weeklyCapacitiesPerType[capacityGroup.id] && (
                                                <Input
                                                    value={
                                                        weeklyCapacitiesPerType[capacityGroup.id].capacities[w]
                                                            ? weeklyCapacitiesPerType[capacityGroup.id].capacities[w]
                                                            : 0
                                                    }
                                                    onChange={(e) => onChange(e, capacityGroup.id, w)}
                                                    name={capacityGroup.id + w}
                                                />
                                            )}
                                        </TableCell>
                                    ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Footer>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="previous"
                        disabled={changingSelection}
                        onClick={() => changeSelection(-3)}
                        startIcon={<SkipPreviousIcon />}>
                        -3kk
                    </Button>
                </Grid>
                {!edit && (
                    <Grid item>
                        <Button variant="contained" color="primary" name="btn-edit" onClick={() => setEdit(true)}>
                            <i className="fas fa-pencil-alt" />
                            &nbsp;{t('buttons.edit')}
                        </Button>
                    </Grid>
                )}
                {edit && (
                    <Grid item>
                        <SaveButton
                            disabled={!hasChanges || saving || changingSelection}
                            hasChanges={hasChanges}
                            onSubmit={saveChanges}
                            saving={saving}
                        />
                    </Grid>
                )}
                {edit && (
                    <Grid item>
                        <Button variant="contained" color="primary" name="btn-cancel" onClick={cancelEdit}>
                            {t('buttons.cancel')}
                        </Button>
                    </Grid>
                )}
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        name="next"
                        disabled={changingSelection}
                        onClick={() => changeSelection(3)}
                        endIcon={<SkipNextIcon />}>
                        +3kk
                    </Button>
                </Grid>
            </Footer>
            <FooterPadding />
        </Grid>
    );
};

export default ProductionPlanning2;
